import { Outlet, createBrowserRouter, useNavigate } from "react-router";
import { AdTags } from "../components/AdTags";
import { LandingScreen } from "../screens/landing/LandingScreen";
import { SignupScreen } from "../screens/onboarding/SignupScreen";
import { ConsentScreen } from "../screens/onboarding/ConsentScreen";
import { OtpVerifyScreen } from "../screens/onboarding/OtpVerifyScreen";
import { AgeCheckScreen } from "../screens/onboarding/AgeCheckScreen";
import { ParentPhoneScreen } from "../screens/onboarding/ParentPhoneScreen";
import { ExamPickScreen } from "../screens/onboarding/ExamPickScreen";
import { SubjectPickScreen } from "../screens/onboarding/SubjectPickScreen";
import { LoginScreen } from "../screens/auth/LoginScreen";
import { OtherLoginsScreen } from "../screens/auth/OtherLoginsScreen";
import { ResetPhoneScreen } from "../screens/auth/ResetPhoneScreen";
import { ClaimEmailScreen } from "../screens/auth/ClaimEmailScreen";
import { NewPasswordScreen } from "../screens/auth/NewPasswordScreen";
import { NotFoundScreen } from "../screens/NotFoundScreen";
import { HomeRoute } from "./routes/HomeRoute";
import { PracticeHubRoute } from "./routes/PracticeHubRoute";
import { DownloadsRoute } from "./routes/DownloadsRoute";
import { WaitingRoomRoute } from "./routes/WaitingRoomRoute";
import { QuestionRoute, TheoryRoute, TopicPickRoute, YearPickRoute } from "./routes/PracticeRoutes";
import { MockResultsRoute, MockRoute } from "./routes/MockRoutes";
import { SummaryRoute } from "./routes/SummaryRoute";
import { ProfileRoute } from "./routes/ProfileRoute";
import { ParentConsentRoute, ParentPayRoute, ParentProgressRoute } from "./routes/ParentRoutes";
import { TutorRoute } from "./routes/TutorRoute";
import { AskParentRoute } from "./routes/AskParentRoute";
import {
  PaymentFailedRoute,
  PaymentPendingRoute,
  PaymentSuccessRoute,
  UpgradeWallRoute,
} from "./routes/PaymentRoutes";
import { PrivacyPolicyScreen } from "../screens/legal/PrivacyPolicyScreen";
import { TermsOfServiceScreen } from "../screens/legal/TermsOfServiceScreen";
import { EditSubjectsScreen } from "../screens/profile/EditSubjectsScreen";

/*
 * Routes are named after what the student is doing, not the spec's screen
 * numbers — those are a build order and would not survive a URL bar.
 *
 * Every route reads from the API. Fixtures live under `tests/` and cannot be
 * imported from here, so a screen showing invented data is a structural
 * impossibility rather than something to remember not to do.
 */

/**
 * Log in, then actually go somewhere.
 *
 * `LoginScreen` calls the API and then its optional `onSubmit`; the router
 * passed none, so a correct password left the student on the login screen with
 * no error and no movement.
 */
function LoginRoute() {
  const navigate = useNavigate();
  return <LoginScreen onSubmit={() => void navigate("/home", { replace: true })} />;
}

export const routes = [
  { path: "/", element: <LandingScreen /> },
  // Consents first, before any personal data; the form follows.
  { path: "/signup", element: <ConsentScreen /> },
  { path: "/signup/details", element: <SignupScreen /> },
  { path: "/verify", element: <OtpVerifyScreen /> },
  { path: "/age", element: <AgeCheckScreen /> },
  { path: "/parent-phone", element: <ParentPhoneScreen /> },
  { path: "/waiting", element: <WaitingRoomRoute /> },
  { path: "/exam", element: <ExamPickScreen /> },
  { path: "/subjects", element: <SubjectPickScreen /> },

  { path: "/practice", element: <PracticeHubRoute /> },
  { path: "/practice/years", element: <YearPickRoute /> },
  { path: "/practice/topics", element: <TopicPickRoute /> },
  { path: "/practice/session", element: <QuestionRoute /> },
  { path: "/practice/theory", element: <TheoryRoute /> },
  { path: "/practice/review", element: <QuestionRoute /> },
  { path: "/summary", element: <SummaryRoute /> },
  { path: "/downloads", element: <DownloadsRoute /> },

  { path: "/mock", element: <MockRoute /> },
  { path: "/mock/results", element: <MockResultsRoute /> },

  { path: "/upgrade", element: <UpgradeWallRoute /> },
  /*
   * The student's side of asking a parent. `/p/pay/:reference` is the parent's
   * side — the wall used to send the student there without a reference, which
   * fell through to not-found.
   */
  { path: "/ask-parent", element: <AskParentRoute /> },
  { path: "/payment/success", element: <PaymentSuccessRoute /> },
  { path: "/payment/pending", element: <PaymentPendingRoute /> },
  { path: "/payment/failed", element: <PaymentFailedRoute /> },

  { path: "/home", element: <HomeRoute /> },
  { path: "/tutor", element: <TutorRoute /> },
  { path: "/profile", element: <ProfileRoute /> },
  { path: "/profile/subjects", element: <EditSubjectsScreen /> },

  { path: "/login", element: <LoginRoute /> },
  { path: "/other-logins", element: <OtherLoginsScreen /> },
  { path: "/claim-email", element: <ClaimEmailScreen /> },
  { path: "/reset", element: <ResetPhoneScreen /> },
  { path: "/reset/new-password", element: <NewPasswordScreen /> },

  { path: "/privacy", element: <PrivacyPolicyScreen /> },
  { path: "/terms", element: <TermsOfServiceScreen /> },

  { path: "/p/consent/:token", element: <ParentConsentRoute /> },
  { path: "/p/pay/:reference", element: <ParentPayRoute /> },
  { path: "/p/progress/:linkId", element: <ParentProgressRoute /> },

  { path: "*", element: <NotFoundScreen /> },
];

/** Every route, under one layout that runs the public-page ad tags. */
function Shell() {
  return (
    <>
      <Outlet />
      <AdTags />
    </>
  );
}

export const router = createBrowserRouter([{ element: <Shell />, children: routes }]);
